import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  FormControl,
  FormLabel,
  InputAdornment,
  Box,
  Paper,
} from '@mui/material';
import PropTypes from 'prop-types';
import Loading from '../../../../components/loading';
import ProcedureCatalogFormModal from '../../../../register/procedures/procedureCatalogFormModal';
import { useNavigate } from 'react-router-dom';
import { useProceduresCatalog } from '../../../../hooks/useProceduresCatalog';
import { useSaveProcedure } from './hooks/useSaveProcedure';
import { useTreatmentProfessional } from '../hooks/useTreatmentProfessional';

export default function ProcedureModalToAesthetic({ isOpen, onClose, treatment, onSaveSuccess }) {
  const [isLoading, setLoading] = useState(false);
  const navigate = useNavigate();

  const { proceduresCatalog: catalog, refetch: fetchCatalog } = useProceduresCatalog();
  const { save: saveProcedureRequest } = useSaveProcedure(treatment?.treatmentId);
  const { professionals, selectedProfessional, setSelectedProfessional } = useTreatmentProfessional(
    treatment,
    isOpen,
  );
  const [selectedCatalog, setSelectedCatalog] = useState(null);
  const [autocompleteInputValue, setAutocompleteInputValue] = useState('');
  const [addCatalogInitialDescription, setAddCatalogInitialDescription] = useState('');
  const [isAddCatalogOpen, setAddCatalogOpen] = useState(false);
  const [observation, setObservation] = useState('');
  const [procedureValue, setProcedureValue] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchCatalog();
    } else {
      setSelectedCatalog(null);
      setObservation('');
      setProcedureValue('');
      setError('');
    }
  }, [isOpen, fetchCatalog]);

  const handleCatalogSaved = (savedCatalog) => {
    fetchCatalog();
    if (savedCatalog) {
      setSelectedCatalog(savedCatalog);
      setAutocompleteInputValue(savedCatalog.description ?? '');
      if (savedCatalog.amount != null) setProcedureValue(String(savedCatalog.amount));
    }
  };

  const validateProcedure = () => {
    if (!selectedCatalog || !procedureValue) {
      setError('É obrigatório informar: Procedimento e Valor para cadastrar um procedimento');
      return false;
    }
    setError('');
    return true;
  };

  const saveProcedure = (event) => {
    event.preventDefault();

    if (!validateProcedure()) {
      return;
    }

    const procedure = {
      procedureCatalogId: selectedCatalog.id,
      dentistId: selectedProfessional?.id ?? null,
      observation: observation,
      amount: procedureValue,
    };
    setLoading(true);
    saveProcedureRequest(procedure)
      .then((returnFromApi) => {
        setLoading(false);
        onSaveSuccess(returnFromApi.data);
      })
      .catch((returnFromApi) => {
        setLoading(false);
        console.error(returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      });
  };

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
        <Loading isToHidden={isLoading === false} />

        <DialogTitle>Procedimento</DialogTitle>
        <form id="new-procedure-form" onSubmit={saveProcedure}>
          <DialogContent>
            <Collapse in={!!error}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            </Collapse>
            <Grid container spacing={0.2}>
              <Grid size={12}>
                <FormControl fullWidth>
                  <FormLabel>Procedimento:</FormLabel>
                  <Autocomplete
                    options={catalog}
                    getOptionLabel={(option) => option.description ?? ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={selectedCatalog}
                    inputValue={autocompleteInputValue}
                    onInputChange={(_, newInputValue) => setAutocompleteInputValue(newInputValue)}
                    onChange={(_, newValue) => {
                      setSelectedCatalog(newValue);
                      if (newValue?.amount != null) setProcedureValue(String(newValue.amount));
                    }}
                    noOptionsText="Nenhum procedimento encontrado."
                    PaperComponent={({ children, ...other }) => (
                      <Paper {...other}>
                        {children}
                        <Box
                          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          <Button
                            fullWidth
                            size="small"
                            onClick={() => {
                              setAddCatalogInitialDescription(autocompleteInputValue);
                              setAddCatalogOpen(true);
                            }}
                          >
                            + Adicionar procedimento
                          </Button>
                        </Box>
                      </Paper>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        data-cy="procedure-description"
                        fullWidth
                        variant="outlined"
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid size={12}>
                <FormControl fullWidth>
                  <FormLabel>Profissional (opcional):</FormLabel>
                  <Autocomplete
                    options={professionals}
                    getOptionLabel={(option) => option.nome ?? ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={selectedProfessional}
                    onChange={(_, newValue) => setSelectedProfessional(newValue)}
                    noOptionsText="Nenhum profissional encontrado."
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        data-cy="procedure-professional"
                        fullWidth
                        variant="outlined"
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid size={12}>
                <FormControl fullWidth>
                  <FormLabel>Observação:</FormLabel>
                  <TextField
                    value={observation}
                    onChange={(e) => setObservation(e.target.value)}
                    fullWidth
                    variant="outlined"
                  />
                </FormControl>
              </Grid>

              <Grid size={12}>
                <FormControl fullWidth>
                  <FormLabel>Valor:</FormLabel>
                  <TextField
                    data-cy="procedure-amount"
                    type="number"
                    value={procedureValue}
                    onChange={(e) => setProcedureValue(e.target.value)}
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                    }}
                    variant="outlined"
                  />
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button variant="outlined" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              data-cy="save-procedure-btn"
              variant="contained"
              type="submit"
              form="new-procedure-form"
            >
              Salvar
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ProcedureCatalogFormModal
        open={isAddCatalogOpen}
        onClose={() => setAddCatalogOpen(false)}
        onSave={handleCatalogSaved}
        initialDescription={addCatalogInitialDescription}
      />
    </>
  );
}

ProcedureModalToAesthetic.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  treatment: PropTypes.shape({
    treatmentId: PropTypes.number.isRequired,
  }).isRequired,
  onSaveSuccess: PropTypes.func.isRequired,
};
